import React, { useEffect, useState } from "react";
import { Check, ExternalLink, BadgeCheck, ShieldCheck, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FREE = [
  "Seven-question diagnostic",
  "Personalized confidence score (out of 100)",
  "Action Roadmap (28 steps)",
  "P&L Decoder",
  "Profit Growth Calculator",
];
const PAID = [
  "Everything in Free, plus:",
  "All eight financial training videos (Parts 1–8)",
  "Every chapter of your FISBO ebook — right inside your dashboard",
  "Lifetime access — no monthly fees, ever",
];

export default function Pricing() {
  const { refreshUser } = useAuth();
  const [price, setPrice] = useState(47);
  const [paypalLink, setPaypalLink] = useState("");
  const [isPro, setIsPro] = useState(false);
  const [paidClicked, setPaidClicked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    client.get("/pro/info").then(({ data }) => { setPrice(data.price); setPaypalLink(data.paypal_link); }).catch(() => {});
    client.get("/lessons").then(({ data }) => setIsPro(data.is_pro)).catch(() => {});
  }, []);

  const goToPayPal = () => {
    if (paypalLink) window.open(paypalLink, "_blank", "noopener");
    setPaidClicked(true);
  };
  const confirmPaid = async () => {
    setUnlocking(true);
    try {
      await client.post("/pro/unlock");
      toast.success("🎉 Unlocked! Lifetime access is yours.");
      setIsPro(true);
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error("Couldn't unlock. Please try again.");
    } finally { setUnlocking(false); }
  };

  return (
    <div className="fade-up space-y-8">
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400">Pricing</p>
        <h1 className="mt-2 font-head text-3xl font-semibold tracking-tight text-neutral-900">Start free. Unlock everything for $47 — once.</h1>
        <p className="mt-2 flex items-center gap-2 text-neutral-600"><Users size={16} className="tenant-text" /> Join hundreds of business owners who've mapped their path to profit.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Free */}
        <div className="rounded-2xl border border-[#E5E2DC] bg-white p-8" data-testid="tier-free">
          <p className="text-sm font-semibold uppercase tracking-widest text-neutral-400">Free</p>
          <p className="mt-2 font-head text-4xl font-semibold text-neutral-900">$0</p>
          <p className="mt-1 text-neutral-500">No credit card required</p>
          <ul className="mt-6 space-y-3">
            {FREE.map((f) => (
              <li key={f} className="flex items-center gap-3 text-neutral-700">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-neutral-200"><Check size={12} className="text-neutral-600" /></span>{f}
              </li>
            ))}
          </ul>
        </div>

        {/* Paid */}
        <div className="relative rounded-2xl border-2 bg-white p-8 tenant-border" data-testid="tier-paid">
          <span className="absolute -top-3 left-8 rounded-full px-3 py-1 text-xs font-semibold text-white tenant-bg">Most popular</span>
          <p className="text-sm font-semibold uppercase tracking-widest tenant-text">FISBO Compass Pro</p>
          <p className="mt-2 font-head text-4xl font-semibold text-neutral-900">${price}<span className="text-lg font-normal text-neutral-500"> one-time</span></p>
          <p className="mt-1 text-neutral-500">Lifetime access via PayPal</p>
          <ul className="mt-6 space-y-3">
            {PAID.map((f, i) => (
              <li key={f} className={`flex items-center gap-3 ${i === 0 ? "font-semibold text-neutral-900" : "text-neutral-700"}`}>
                {i !== 0 && <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full tenant-bg"><Check size={12} className="text-white" /></span>}
                {i === 0 ? f : f}
              </li>
            ))}
          </ul>

          {isPro ? (
            <div className="mt-7 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold tenant-text" style={{ backgroundColor: "var(--tenant-primary-light)" }} data-testid="pricing-pro-badge">
              <ShieldCheck size={16} /> You have lifetime access
            </div>
          ) : (
            <div className="mt-7 space-y-3">
              {!paidClicked ? (
                <button data-testid="pricing-unlock-btn" onClick={goToPayPal} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>
                  Unlock Lifetime Access — ${price} <ExternalLink size={16} />
                </button>
              ) : (
                <div className="space-y-3 rounded-xl border border-[#E5E2DC] bg-[#FAF9F6] p-4">
                  <p className="text-sm text-neutral-700">Finished paying on PayPal? Confirm below to unlock instantly.</p>
                  <button data-testid="pricing-confirm-btn" onClick={confirmPaid} disabled={unlocking} className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60" style={{ backgroundColor: "var(--tenant-primary)" }}>
                    {unlocking ? "Unlocking…" : "I've completed my PayPal payment"}
                  </button>
                  <button onClick={goToPayPal} className="w-full text-sm text-neutral-500 underline">Re-open PayPal checkout</button>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-sm text-neutral-500">
                <BadgeCheck size={15} className="tenant-text" /> 30-day money-back guarantee
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="flex items-center gap-2 text-sm text-neutral-500">
        <Sparkles size={14} className="tenant-text" /> Secure checkout via PayPal · Works on any device · Built by coaches who've run real companies.
      </p>
    </div>
  );
}
